import { useState } from "react";
import {
    Container,
    Row,
    Col
} from "reactstrap";

import {
    FiChevronDown,
    FiCheck,
    FiBookOpen
} from "react-icons/fi";

import "./CourseSyllabus.css";

function CourseSyllabus({
    courseName,
    syllabus = []
}) {

    // Only ONE module can be open at a time
    const [activeModule, setActiveModule] = useState(null);

    /*
     * Supports:
     *
     * 1. Single syllabus
     *
     * syllabus = [
     *   {
     *      number: 1,
     *      title: "...",
     *      topics: [...]
     *   }
     * ]
     *
     *
     * 2. Multiple syllabus programs
     *
     * syllabus = [
     *   {
     *      name: "Program 1",
     *      modules: [...]
     *   },
     *   {
     *      name: "Program 2",
     *      modules: [...]
     *   }
     * ]
     */

    const syllabusPrograms =
        Array.isArray(syllabus) &&
        syllabus.length > 0 &&
        syllabus[0]?.modules
            ? syllabus
            : [
                {
                    name: courseName || "Course",
                    modules: Array.isArray(syllabus)
                        ? syllabus
                        : []
                }
            ];


    /*
     * Open / Close module
     *
     * Example:
     * programIndex = 0
     * moduleIndex = 2
     *
     * key = "0-2"
     */

    const toggleModule = (programIndex, moduleIndex) => {

        const key = `${programIndex}-${moduleIndex}`;

        setActiveModule((current) =>
            current === key ? null : key
        );
    };


    return (
        <section className="course-syllabus">

            <Container>

                {syllabusPrograms.map(
                    (program, programIndex) => {

                        // Safety check
                        const modules = Array.isArray(program?.modules)
                            ? program.modules
                            : [];

                        return (

                            <div
                                className="course-syllabus-program"
                                key={`${program?.name || "program"}-${programIndex}`}
                            >

                                {/* ==============================
                                    SYLLABUS HEADER
                                ============================== */}

                                <div className="course-syllabus-header">

                                    <span className="course-syllabus-label">
                                        COURSE CURRICULUM
                                    </span>

                                    <h2>
                                        Syllabus{" "}
                                        <strong>
                                            {program?.name || courseName}
                                        </strong>
                                    </h2>

                                    <p>
                                        Explore the modules and topics covered
                                        in this training program.
                                    </p>

                                </div>


                                {/* ==============================
                                    EMPTY SYLLABUS CHECK
                                ============================== */}

                                {modules.length === 0 ? (

                                    <div className="syllabus-empty">
                                        No syllabus modules available.
                                    </div>

                                ) : (

                                    /* ==============================
                                       MODULE CARDS
                                    ============================== */

                                    <Row className="g-4">

                                        {modules.map(
                                            (module, moduleIndex) => {

                                                // Unique key
                                                const moduleKey =
                                                    `${programIndex}-${moduleIndex}`;

                                                const isActive =
                                                    activeModule === moduleKey;

                                                const topics =
                                                    Array.isArray(module?.topics)
                                                        ? module.topics
                                                        : [];

                                                const topicCount =
                                                    topics.length;

                                                return (

                                                    <Col
                                                        lg="6"
                                                        md="6"
                                                        sm="12"
                                                        key={moduleKey}
                                                    >

                                                        <div
                                                            className={`syllabus-card ${
                                                                isActive
                                                                    ? "syllabus-card-active"
                                                                    : ""
                                                            }`}
                                                        >

                                                            {/* ==============================
                                                                CARD HEADER
                                                            ============================== */}

                                                            <button
                                                                type="button"
                                                                className="syllabus-card-header"
                                                                onClick={() =>
                                                                    toggleModule(
                                                                        programIndex,
                                                                        moduleIndex
                                                                    )
                                                                }
                                                                aria-expanded={
                                                                    isActive
                                                                }
                                                            >

                                                                <div className="syllabus-card-info">

                                                                    {/* Number */}

                                                                    <div className="syllabus-card-number">
                                                                        {String(
                                                                            module?.number ??
                                                                            moduleIndex + 1
                                                                        ).padStart(
                                                                            2,
                                                                            "0"
                                                                        )}
                                                                    </div>


                                                                    {/* Icon */}

                                                                    <div className="syllabus-card-icon">
                                                                        <FiBookOpen />
                                                                    </div>


                                                                    {/* Title */}

                                                                    <div className="syllabus-card-title-wrapper">

                                                                        <h3>
                                                                            {module?.title ||
                                                                                `Module ${
                                                                                    moduleIndex +
                                                                                    1
                                                                                }`}
                                                                        </h3>

                                                                        <span>
                                                                            {
                                                                                topicCount
                                                                            }{" "}
                                                                            {topicCount ===
                                                                            1
                                                                                ? "Topic"
                                                                                : "Topics"}
                                                                        </span>

                                                                    </div>

                                                                </div>


                                                                {/* Arrow */}

                                                                <div
                                                                    className={`syllabus-card-toggle ${
                                                                        isActive
                                                                            ? "syllabus-card-toggle-active"
                                                                            : ""
                                                                    }`}
                                                                >

                                                                    <FiChevronDown />

                                                                </div>

                                                            </button>


                                                            {/* ==============================
                                                                EXPANDED CONTENT
                                                            ============================== */}

                                                            {isActive && (

                                                                <div className="syllabus-card-content">

                                                                    {topics.length ===
                                                                    0 ? (

                                                                        <div className="syllabus-no-topics">
                                                                            No topics
                                                                            available
                                                                            for this
                                                                            module.
                                                                        </div>

                                                                    ) : (

                                                                        <div className="syllabus-topic-list">

                                                                            {topics.map(
                                                                                (
                                                                                    topic,
                                                                                    topicIndex
                                                                                ) => {

                                                                                    /*
                                                                                     * Nested topic
                                                                                     *
                                                                                     * {
                                                                                         title: "...",
                                                                                         subTopics: [...]
                                                                                       }
                                                                                     */

                                                                                    if (
                                                                                        typeof topic ===
                                                                                            "object" &&
                                                                                        topic !==
                                                                                            null
                                                                                    ) {

                                                                                        const subTopics =
                                                                                            Array.isArray(
                                                                                                topic.subTopics
                                                                                            )
                                                                                                ? topic.subTopics
                                                                                                : [];

                                                                                        return (

                                                                                            <div
                                                                                                className="syllabus-nested-topic"
                                                                                                key={`${moduleKey}-topic-${topicIndex}`}
                                                                                            >

                                                                                                <div className="syllabus-topic-main">

                                                                                                    <FiCheck />

                                                                                                    <span>
                                                                                                        {
                                                                                                            topic.title
                                                                                                        }
                                                                                                    </span>

                                                                                                </div>


                                                                                                {subTopics.length >
                                                                                                    0 && (

                                                                                                    <div className="syllabus-subtopics">

                                                                                                        {subTopics.map(
                                                                                                            (
                                                                                                                subTopic,
                                                                                                                subIndex
                                                                                                            ) => (

                                                                                                                <div
                                                                                                                    className="syllabus-subtopic"
                                                                                                                    key={`${moduleKey}-sub-${topicIndex}-${subIndex}`}
                                                                                                                >
                                                                                                                    {
                                                                                                                        subTopic
                                                                                                                    }
                                                                                                                </div>

                                                                                                            )
                                                                                                        )}

                                                                                                    </div>

                                                                                                )}

                                                                                            </div>

                                                                                        );
                                                                                    }


                                                                                    /*
                                                                                     * Normal topic
                                                                                     */

                                                                                    return (

                                                                                        <div
                                                                                            className="syllabus-topic"
                                                                                            key={`${moduleKey}-topic-${topicIndex}`}
                                                                                        >

                                                                                            <FiCheck />

                                                                                            <span>
                                                                                                {
                                                                                                    topic
                                                                                                }
                                                                                            </span>

                                                                                        </div>

                                                                                    );

                                                                                }
                                                                            )}

                                                                        </div>

                                                                    )}

                                                                </div>

                                                            )}

                                                        </div>

                                                    </Col>

                                                );
                                            }
                                        )}

                                    </Row>

                                )}

                            </div>

                        );
                    }
                )}

            </Container>

        </section>
    );
}

export default CourseSyllabus;